import React, { useState, useMemo, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const OptimizedQuillEditor = ({ initialContent = '' }) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Debounce content updates to prevent excessive re-renders
  useEffect(() => {
    let timeoutId;
    if (initialContent) {
      setIsLoading(true);
      // Break up the content loading into chunks
      timeoutId = setTimeout(() => {
        setContent(initialContent);
        setIsLoading(false);
      }, 100);
    }
    return () => clearTimeout(timeoutId);
  }, [initialContent]);

  // Memoize Quill modules to prevent re-creation
  const modules = useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ],
    clipboard: {
      matchVisual: false, // Improves paste performance
    },
    history: {
      delay: 2000,
      maxStack: 500,
      userOnly: true
    }
  }), []);

  // Memoize formats to prevent re-creation
  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet'
  ], []);

  if (isLoading) {
    return (
      <div className="w-full h-64 border rounded-lg flex items-center justify-center bg-gray-50">
        <div className="text-gray-500">Loading editor...</div>
      </div>
    );
  }

  return (
    <div className="editor-wrapper">
      <ReactQuill
        value={content}
        onChange={setContent}
        modules={modules}
        formats={formats}
        theme="snow"
        bounds=".editor-wrapper"
        placeholder="Start typing..."
      />
    </div>
  );
};

export default OptimizedQuillEditor;